/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useRef, useState } from "react";
import { faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PropTypes } from "prop-types";
import { useHistory } from "react-router-dom";
import { Formik } from "formik";
import {
  Content,
  Heading,
  ColIcon,
  RowIcon,
  FilterContainer,
  FilterSearchContainer,
  MobileLabel,
  RowFilter,
  CheckBoxColumn,
  FilterColumn,
  FieldGrid,

} from "./styles";
import { Icon, Button, SearchField } from "components/index";
import Row from "components/Grid/Row";
import Col from "components/Grid/Col";
import { Label, SelectField } from "components/Form";
import Checkbox from "components/Form/CheckBox";
import MultiSelectField from "components/Form/MultiSelectField";
import { toLower } from "lodash";

const propTypes = {
  title: PropTypes.string,
  children: PropTypes.any,
  icon: PropTypes.bool,
};

export default function ContentCard({ title, children, info, filterOptions, handleFilter }) {

  const history = useHistory();
  const formik = useRef();
  const [showFilter, setShowFilter] = useState(false);
  const [insurersOptions, setInsurersOptions] = useState(filterOptions.insurers || []);

  const initialValues = {
    insurer: "",
    facility: "",
    currency: "",
    status: "",
  }

  function resetFilters() {
    formik.current.resetForm(initialValues);
    handleFilter(initialValues);
  }

  const statusOptions = [
    { label: 'Resent', value: 'Resent' },
    { label: 'Sent', value: 'Sent' },
    { label: 'Indicated', value: 'Indicated' },
    { label: 'Declined', value: 'Declined' },
    { label: 'Interested', value: 'Interested' },
  ];

  const handleInsurerSearch = (value) => {
    const filtered = filterOptions.insurers.filter(option => toLower(option.personName).includes(toLower(value)))
    setInsurersOptions(filtered)
  }

  return (
    <>
      <Heading>
        {title ? title : "Content Title"}
        <RowIcon>
          <Formik
            initialValues={initialValues}
            innerRef={formik}
            onSubmit={(values) => handleFilter(values)}
          >
            {(formikProps) => (
              <>
                <Row style={{ alignItems: 'center' }}>
                  <ColIcon>
                    <Icon
                      name="filter"
                      onClick={() => setShowFilter(!showFilter)}
                    />
                  </ColIcon>
                  <ColIcon>
                    <Icon
                      name="filterCan"
                      onClick={() => resetFilters()}
                    />
                  </ColIcon>

                  <ColIcon>
                    <div
                      onClick={() =>
                        history.push(
                          `/broking/document-management/${info.idOperation}`
                        )
                      }
                    >
                      <FontAwesomeIcon
                        icon={faFolderOpen}
                        size="2x"
                        color="#FFF"
                        cursor="pointer"
                      />
                    </div>
                  </ColIcon>
                </Row>

                <FilterContainer
                  className={
                    showFilter ? "filter-open close" : "filter-close"
                  }
                >
                  <Row>
                    <Col
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Icon
                        name="close"
                        className="icon-close"
                        onClick={() => setShowFilter(false)}
                      />
                    </Col>
                  </Row>
                  <FilterSearchContainer>
                    <Row>
                      <MobileLabel>FILTER</MobileLabel>
                    </Row>
                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                            checked={formikProps.values.insurer}
                            onChange={() =>
                              formikProps?.setFieldValue("insurer", "")
                            }
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="insurer"
                          placeholder="Insurer"
                          component={SearchField}
                          autoComplete
                          value={formikProps?.values.insurer?.personName || formikProps?.values.insurer}
                          onChange={handleInsurerSearch}
                          searchList={insurersOptions}
                          filterModal
                          onPressEnter={() => handleFilter()}
                          onSearchSelect={selectedValue => formikProps?.setFieldValue('insurer', selectedValue)}
                          onFocus={() => setInsurersOptions(filterOptions.insurers)}
                        />
                      </FilterColumn>
                    </RowFilter>

                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                            checked={formikProps.values.facility.length}
                            onChange={() =>
                              formikProps?.setFieldValue("facility", "")
                            }
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="facility"
                          placeholder="Facility"
                          component={MultiSelectField}
                          corDiferente={false}
                          options={filterOptions.facilities}
                          onPressEnter={() => handleFilter()}
                          format={(value) => {
                            const selectedFacility = filterOptions.facilities.filter(option => value.includes(option.value))
                            return selectedFacility.map(item => item.label).join(', ')
                          }}
                        />
                      </FilterColumn>
                    </RowFilter>

                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                            checked={formikProps.values.currency.length}
                            onChange={() =>
                              formikProps?.setFieldValue("currency", "")
                            }
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="currency"
                          placeholder="Currency"
                          component={MultiSelectField}
                          options={filterOptions.currencies}
                          onPressEnter={() => handleFilter()}
                          format={(value) => {
                            const selectedCurrency = filterOptions.currencies.filter(option => value.includes(option.value))
                            return selectedCurrency.map(item => item.label).join(', ')
                          }}
                        />
                      </FilterColumn>
                    </RowFilter>

                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                            checked={formikProps.values.status.length}
                            onChange={() =>
                              formikProps?.setFieldValue("status", "")
                            }
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="status"
                          placeholder="Status"
                          component={MultiSelectField}
                          options={statusOptions}
                          format={(value) => {
                            const selectedStatus = statusOptions.filter(option => value.includes(option.value))
                            return selectedStatus.map(item => item.label).join(', ')
                          }}
                        />
                      </FilterColumn>
                    </RowFilter>
                  </FilterSearchContainer>

                  <Row align="center" className="apply-button-container">
                    <Col>
                      <Button
                        primary
                        type="button"
                        id="btnFilter"
                        style={{ textAlign: "center" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setShowFilter(false);
                          formikProps.handleSubmit();
                        }}
                      >
                        Apply
                      </Button>
                    </Col>
                  </Row>
                </FilterContainer>
              </>
            )}
          </Formik>
        </RowIcon>
      </Heading>
      <Content>{children}</Content>
    </>
  );
}
ContentCard.propTypes = propTypes;
